import { useMemo } from 'react';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  CalendarClock,
  FileSignature,
  Scale,
  Wallet,
} from 'lucide-react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@solara/ui-shared';
import { useI18n, useTranslation } from '@solara/i18n';
import { useVatOverview } from '@solara/api-client/react';
import type { VatPeriodDto } from '@solara/api-client';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function VatCenterPage() {
  const { t } = useTranslation('tickstar');
  const { locale } = useI18n();

  const aedFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }),
    [locale],
  );
  const numberFmt = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const pctFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }),
    [locale],
  );
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
    [locale],
  );

  const vatQuery = useVatOverview();
  if (vatQuery.isLoading) return <LoadingBlock />;
  if (vatQuery.error) return <ErrorBanner error={vatQuery.error} />;
  const raw = vatQuery.data as
    | (typeof vatQuery.data & {
        ytdOutputVatAedMinor?: string;
        ytdInputVatAedMinor?: string;
        ytdNetPayableAedMinor?: string;
        periods?: VatPeriodDto[];
      })
    | undefined;
  if (!raw) return null;

  // Backend returns minor-unit BigInt strings + a single `periods` array.
  // Adapt to the shape the rest of the page expects (AED units + monthly/quarterly split).
  const toAed = (v: unknown) => (typeof v === 'string' ? Number(v) / 100 : typeof v === 'number' ? v : 0);
  const rawPeriods = (raw.periods ?? raw.monthly ?? raw.quarterly ?? []) as Array<
    VatPeriodDto & {
      kind?: string;
      outputVatMinor?: string;
      inputVatMinor?: string;
      netPayableMinor?: string;
      tddDeadline?: string;
      invoicesCount?: number;
    }
  >;
  const adaptPeriod = (p: (typeof rawPeriods)[number]): VatPeriodDto => ({
    id: p.id,
    tenantId: p.tenantId,
    periodKind: ((p.kind ?? p.periodKind ?? 'monthly').toUpperCase()) as VatPeriodDto['periodKind'],
    periodLabel: p.periodLabel,
    outputVatAed: toAed(p.outputVatMinor ?? p.outputVatAed),
    inputVatAed: toAed(p.inputVatMinor ?? p.inputVatAed),
    netPayableAed: toAed(p.netPayableMinor ?? p.netPayableAed),
    tddStatus: (p.tddStatus ?? 'pending') as VatPeriodDto['tddStatus'],
    deadline: p.tddDeadline ?? p.deadline ?? new Date().toISOString(),
    invoiceCount: p.invoicesCount ?? p.invoiceCount ?? 0,
  });
  const adapted = rawPeriods.map(adaptPeriod);
  const data = {
    ytdOutputVatAed: toAed(raw.ytdOutputVatAedMinor ?? raw.ytdOutputVatAed),
    ytdInputVatAed: toAed(raw.ytdInputVatAedMinor ?? raw.ytdInputVatAed),
    ytdNetPayableAed: toAed(raw.ytdNetPayableAedMinor ?? raw.ytdNetPayableAed),
    refundExpectedAed: toAed(raw.refundExpectedAed),
    pendingTdd: raw.pendingTdd ?? 0,
    monthly: adapted.filter((p) => p.periodKind === 'MONTHLY'),
    quarterly: adapted.filter((p) => p.periodKind === 'QUARTERLY'),
  };

  const netTone: 'danger' | 'success' = data.ytdNetPayableAed > 0 ? 'danger' : 'success';

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('modules.vat.title')}</h1>
          <p className="text-fg-muted text-sm mt-1">{t('modules.vat.subtitle')}</p>
        </div>
      </header>

      {/* KPI grid */}
      <section className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          icon={<ArrowUpCircle className="h-5 w-5" />}
          tone="brand"
          label={t('modules.vat.ytdOutput')}
          value={aedFmt.format(data.ytdOutputVatAed)}
        />
        <KpiCard
          icon={<ArrowDownCircle className="h-5 w-5" />}
          tone="info"
          label={t('modules.vat.ytdInput')}
          value={aedFmt.format(data.ytdInputVatAed)}
        />
        <KpiCard
          icon={<Scale className="h-5 w-5" />}
          tone={netTone}
          label={t('modules.vat.ytdNet')}
          value={aedFmt.format(data.ytdNetPayableAed)}
        />
        <KpiCard
          icon={<Wallet className="h-5 w-5" />}
          tone="success"
          label={t('modules.vat.refundExpected')}
          value={aedFmt.format(data.refundExpectedAed)}
        />
        <KpiCard
          icon={
            data.pendingTdd > 0 ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <FileSignature className="h-5 w-5" />
            )
          }
          tone={data.pendingTdd > 0 ? 'warning' : 'success'}
          label={t('modules.vat.pendingTdd')}
          value={numberFmt.format(data.pendingTdd)}
        />
      </section>

      {/* Tabs: monthly / quarterly */}
      <Tabs defaultValue="monthly">
        <TabsList>
          <TabsTrigger value="monthly">{t('modules.vat.monthly')}</TabsTrigger>
          <TabsTrigger value="quarterly">{t('modules.vat.quarterly')}</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly">
          <PeriodTable periods={data.monthly} t={t} aedFmt={aedFmt} dateFmt={dateFmt} numberFmt={numberFmt} />
        </TabsContent>
        <TabsContent value="quarterly">
          <PeriodTable periods={data.quarterly} t={t} aedFmt={aedFmt} dateFmt={dateFmt} numberFmt={numberFmt} />
        </TabsContent>
      </Tabs>

      {/* White-label decision (2026-05-14): no provider pill on customer-facing pages. */}
    </div>
  );
}

function PeriodTable({
  periods,
  t,
  aedFmt,
  dateFmt,
  numberFmt,
}: {
  periods: VatPeriodDto[];
  t: (k: string, vars?: Record<string, unknown>) => string;
  aedFmt: Intl.NumberFormat;
  dateFmt: Intl.DateTimeFormat;
  numberFmt: Intl.NumberFormat;
}) {
  return (
    <Card className="rounded-2xl">
      <CardBody>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-fg-subtle">
                <th className="text-start font-medium pb-2">{t('modules.vat.header.period')}</th>
                <th className="text-end font-medium pb-2">{t('modules.vat.header.output')}</th>
                <th className="text-end font-medium pb-2">{t('modules.vat.header.input')}</th>
                <th className="text-end font-medium pb-2">{t('modules.vat.header.net')}</th>
                <th className="text-start font-medium pb-2">{t('modules.vat.header.tdd')}</th>
                <th className="text-start font-medium pb-2">{t('modules.vat.header.deadline')}</th>
                <th className="text-end font-medium pb-2">{t('modules.vat.header.invoices')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {periods.map((p) => (
                <PeriodRow
                  key={p.id}
                  p={p}
                  t={t}
                  aedFmt={aedFmt}
                  dateFmt={dateFmt}
                  numberFmt={numberFmt}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function PeriodRow({
  p,
  t,
  aedFmt,
  dateFmt,
  numberFmt,
}: {
  p: VatPeriodDto;
  t: (k: string, vars?: Record<string, unknown>) => string;
  aedFmt: Intl.NumberFormat;
  dateFmt: Intl.DateTimeFormat;
  numberFmt: Intl.NumberFormat;
}) {
  const tddTone =
    p.tddStatus === 'submitted' ? 'success' : p.tddStatus === 'pending' ? 'warning' : 'danger';
  const tddLabel =
    p.tddStatus === 'submitted'
      ? t('modules.vat.tddStatus.submitted')
      : p.tddStatus === 'pending'
      ? t('modules.vat.tddStatus.pending')
      : t('modules.vat.tddStatus.overdue');
  const netClass = p.netPayableAed >= 0 ? 'text-fg' : 'text-success';

  return (
    <tr>
      <td className="py-2 font-medium tabular-nums">{p.periodLabel}</td>
      <td className="py-2 text-end tabular-nums">{aedFmt.format(p.outputVatAed)}</td>
      <td className="py-2 text-end tabular-nums">{aedFmt.format(p.inputVatAed)}</td>
      <td className={cn('py-2 text-end tabular-nums font-medium', netClass)}>
        {aedFmt.format(p.netPayableAed)}
      </td>
      <td className="py-2">
        <Badge tone={tddTone}>{tddLabel}</Badge>
      </td>
      <td className="py-2 tabular-nums text-xs">
        <span className="inline-flex items-center gap-1">
          <CalendarClock className="h-3 w-3 text-fg-subtle" />
          {dateFmt.format(new Date(p.deadline))}
        </span>
      </td>
      <td className="py-2 text-end text-xs tabular-nums">
        {numberFmt.format(p.invoiceCount)}
      </td>
    </tr>
  );
}

function KpiCard({
  icon,
  tone,
  label,
  value,
}: {
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'info' | 'warning' | 'danger';
  label: string;
  value: string;
}) {
  const accent: Record<typeof tone, string> = {
    brand: 'text-brand-600 bg-brand-50',
    success: 'text-success bg-emerald-50',
    info: 'text-info bg-cyan-50',
    warning: 'text-warning bg-amber-50',
    danger: 'text-danger bg-red-50',
  };
  const valueTone: Record<typeof tone, string> = {
    brand: '',
    success: 'text-success',
    info: '',
    warning: '',
    danger: 'text-danger',
  };
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex items-start gap-3">
        <div className={cn('h-10 w-10 grid place-items-center rounded-xl shrink-0', accent[tone])}>
          {icon}
        </div>
        <div className="flex flex-col leading-tight min-w-0 flex-1">
          <span
            className="text-[11px] sm:text-xs uppercase tracking-wide text-fg-subtle leading-snug line-clamp-2"
            title={label}
          >
            {label}
          </span>
          <span className={cn('mt-1 text-lg sm:text-xl font-semibold tabular-nums break-words', valueTone[tone])}>
            {value}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
